import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowLeft, User, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog.php?slug=${slug}`).then((res) => {
      setBlog(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 animate-pulse space-y-6">
          <div className="h-8 bg-gray-100 rounded w-3/4" />
          <div className="h-64 bg-gray-100 rounded-2xl" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-100 rounded" />
            <div className="h-4 bg-gray-100 rounded w-5/6" />
            <div className="h-4 bg-gray-100 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="pt-20 min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-['Outfit'] text-2xl font-semibold text-[#0B1F3A] mb-2">Article Not Found</h2>
          <p className="text-gray-500 mb-4">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-[#3B82F6] font-medium flex items-center gap-1 justify-center">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page" className="pt-20 bg-white">
      {/* Header */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#F9FAFB] via-white to-[#EFF6FF]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/blog" className="text-sm text-[#3B82F6] font-medium flex items-center gap-1 mb-6 hover:gap-2 transition-all" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3B82F6] bg-[#3B82F6]/5 px-2.5 py-1 rounded-full">
              <Tag className="w-3 h-3" /> {blog.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar className="w-3 h-3" /> {formatDate(blog.created_at)}
            </span>
          </div>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0B1F3A] mb-4" data-testid="blog-detail-title">
            {blog.title}
          </h1>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <User className="w-4 h-4" /> {blog.author}
          </div>
        </div>
      </section>

      {/* Image */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 -mt-2">
        <img
          src={blog.image_url}
          alt={blog.title}
          className="w-full rounded-2xl shadow-lg object-cover aspect-video"
          data-testid="blog-detail-image"
        />
      </div>

      {/* Content */}
      <section className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none" data-testid="blog-detail-content">
            {blog.content.split('\n\n').map((para, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-5">{para}</p>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-[#F9FAFB] rounded-2xl border border-gray-100">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#0B1F3A] mb-2">
              Need Personalized Advice?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Get a free consultation with Sagar Salunkhe to discuss your financial goals.
            </p>
            <a
              href="whatsapp://send?phone=919324555493&text=Hi%20Sagar,%20I%20read%20your%20article%20and%20need%20advice."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-2.5 rounded-full text-sm hover:bg-[#20BD5A] transition-all"
              data-testid="blog-detail-cta"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
